/**
 * Constantes del flujo de reserva.
 *
 * Centraliza valores que antes vivían como strings mágicos repartidos por
 * los componentes (`'cash'`, `'night'`, `'USD'`, `activeStep === 2`, etc.).
 * Cualquier nuevo valor de dominio (un nuevo método de pago, una nueva
 * moneda) se agrega aquí y se propaga al resto del módulo.
 */

/** Pasos del wizard. Indexados en 0 porque MUI Stepper y el activeStep así lo manejan. */
export const STEPS = Object.freeze({
  ROOM: 0,
  CUSTOMER: 1,
  PAYMENT: 2
});

export const TOTAL_STEPS = 3;

/** Métodos de pago soportados. Coinciden con los strings que espera el backend. */
export const PAYMENT_METHODS = Object.freeze({
  CASH: 'cash',
  CARD: 'card',
  TRANSFER: 'transfer',
  ZELLE: 'zelle',
  OTHER: 'other'
});

/** Etiquetas legibles por humanos (ES) para cada método. */
export const PAYMENT_METHOD_LABELS = Object.freeze({
  [PAYMENT_METHODS.CASH]: 'Efectivo',
  [PAYMENT_METHODS.CARD]: 'Tarjeta',
  [PAYMENT_METHODS.TRANSFER]: 'Transferencia',
  [PAYMENT_METHODS.ZELLE]: 'Zelle',
  [PAYMENT_METHODS.OTHER]: 'Otro'
});

/** Iconos (emoji) para los selectores. Mantenidos por retrocompatibilidad visual. */
export const PAYMENT_METHOD_ICONS = Object.freeze({
  [PAYMENT_METHODS.CASH]: '💵',
  [PAYMENT_METHODS.CARD]: '💳',
  [PAYMENT_METHODS.TRANSFER]: '🏦',
  [PAYMENT_METHODS.ZELLE]: '💸',
  [PAYMENT_METHODS.OTHER]: '📝'
});

/** Métodos que requieren capturar número de transacción. */
export const METHODS_REQUIRING_TRANSACTION = Object.freeze([
  PAYMENT_METHODS.CARD,
  PAYMENT_METHODS.TRANSFER,
  PAYMENT_METHODS.ZELLE
]);

/** Métodos que requieren capturar número de lote. */
export const METHODS_REQUIRING_BATCH = Object.freeze([PAYMENT_METHODS.CARD]);

/** Modo de pago: simple o dividido en varios. */
export const PAYMENT_MODES = Object.freeze({
  SIMPLE: 'simple',
  SPLIT: 'split'
});

/** Tipo de estancia legacy. Los hoteles con configuración dinámica usan IDs distintos. */
export const STAY_TYPE_NIGHT = 'night';
export const STAY_TYPE_PARTIAL = 'partial';

/** Moneda base del sistema. */
export const BASE_CURRENCY = 'USD';

/** Tipos de identificación soportados. */
export const ID_CARD_TYPES = Object.freeze({
  DEFAULT: 'default',
  ID: 'ID',
  PASSPORT: 'Passport',
  RIF: 'Rif',
  DRIVING_LICENSE: 'DrivingLicense'
});

/** Etiquetas legibles por humanos (ES) para tipos de ID. */
export const ID_CARD_TYPE_LABELS = Object.freeze({
  [ID_CARD_TYPES.DEFAULT]: 'Seleccione tipo',
  [ID_CARD_TYPES.ID]: 'Cédula de Identidad',
  [ID_CARD_TYPES.PASSPORT]: 'Pasaporte',
  [ID_CARD_TYPES.RIF]: 'RIF',
  [ID_CARD_TYPES.DRIVING_LICENSE]: 'Licencia de Conducir'
});

/** Mensaje legible para un método de pago, con fallback. */
export const getPaymentMethodLabel = (method) =>
  PAYMENT_METHOD_LABELS[method] || 'No especificado';

/** Mensaje con icono para un método de pago. */
export const getPaymentMethodLabelWithIcon = (method) => {
  const label = PAYMENT_METHOD_LABELS[method];
  if (!label) return 'No especificado';
  return `${PAYMENT_METHOD_ICONS[method] || ''} ${label}`.trim();
};

/** Forma vacía de un cliente. Útil al inicializar Formik y al agregar huéspedes. */
export const emptyCustomer = () => ({
  phoneNumber: '',
  firstName: '',
  lastName: '',
  email: '',
  idCardType: ID_CARD_TYPES.DEFAULT,
  idcardNumber: '',
  idFile: '',
  address: ''
});

/** Forma vacía de un split payment. ID basado en timestamp para keys de React. */
export const emptySplitPayment = () => ({
  id: Date.now() + Math.floor(Math.random() * 1000), // evita colisiones cuando se crean varios en el mismo ms
  paymentMethod: '',
  amount: '',
  currency: BASE_CURRENCY,
  exchangeRateUsed: 1,
  amountInForeignCurrency: 0,
  batchNumber: '',
  transactionNumber: ''
});
